import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { IDraftingComments } from "@shared/lib/types/drafting";
import { Input } from "@shared/ui";
import { Space } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import {
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  MessageSquare,
  Pencil,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";

const formatTime = (seconds: number) => {
  if (seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

interface DraftFeedbackProps {
  comments: IDraftingComments[];
  currentTime: number;
  onPost: (text: string, isGeneral: boolean) => void;
  onJump: (time: number) => void;
  isDrawingEnabled: boolean;
  onDrawingEnabledChange: (enabled: boolean) => void;
  onSelectComment: (commentId: string, time: number) => void;
  onClearDraftDrawings: () => void;
  isCreating: boolean;
  isTimeFrameAsset: boolean;
  draftingId: string;
  draftingVersionId: string;
  mediaAssetId: string;
  isOtherAsset?: boolean;
  isGeneralMode: boolean;
  setIsGeneralMode: (isGeneral: boolean) => void;
  onResolve?: (commentId: string, isResolved: boolean) => void;
  isResolving?: boolean;
  onDelete?: (commentId: string) => void;
  isDeleting?: boolean;
  transcription?: {
    start: number;
    end: number;
    text: string;
    _id: string;
  }[];
  transcriptionStatus?: string;
}

export const DraftFeedback = ({
  comments,
  currentTime,
  onPost,
  onJump,
  isDrawingEnabled,
  onDrawingEnabledChange,
  onSelectComment,
  onClearDraftDrawings,
  isCreating,
  isTimeFrameAsset,
  isOtherAsset,
  isGeneralMode,
  setIsGeneralMode,
  onResolve,
  isResolving,
  onDelete,
  isDeleting,
  transcription,
  transcriptionStatus,
}: DraftFeedbackProps) => {
  const { userDetails } = useGetMe();
  const [commentDraft, setCommentDraft] = useState("");
  const [activeRightTab, setActiveRightTab] = useState<
    "comments" | "transcript"
  >("comments");
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const isGeneralComment = isGeneralMode;

  useEffect(() => {
    if (!isTimeFrameAsset) {
      setIsGeneralMode(true);
    }
  }, [isTimeFrameAsset, setIsGeneralMode]);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments.length]);

  const handlePostComment = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!commentDraft.trim()) return;
    onPost(commentDraft.trim(), isGeneralComment);
    setCommentDraft("");
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommentDraft(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
  };

  const handleResolveComment = (e: React.MouseEvent, commentId: string) => {
    e.stopPropagation();
    const comment = comments.find((c) => c._id === commentId);
    if (comment && onResolve) {
      onResolve(commentId, !comment.isResolved);
    }
  };

  const handleDeleteComment = (e: React.MouseEvent, commentId: string) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(commentId);
    }
  };

  const jumpToTime = (time: number, commentId?: string) => {
    if (commentId) {
      setActiveCommentId(commentId);
      onSelectComment(commentId, time);
    }
    onJump(time);
  };

  const isUserComment = (comment: IDraftingComments) => {
    return comment.commentedBy?._id === userDetails?.userEntity?.id;
  };

  const allComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
      if (Number.isNaN(aDate)) return 1;
      if (Number.isNaN(bDate)) return -1;
      return aDate - bDate;
    });
  }, [comments]);

  const transcriptLines = useMemo(() => {
    if (!transcription) return [];
    return transcription.map((segment) => {
      const mins = Math.floor(segment.start / 60);
      const secs = Math.floor(segment.start % 60);
      return {
        seconds: segment.start,
        timeStr: `${mins}:${secs.toString().padStart(2, "0")}`,
        text: segment.text,
      };
    });
  }, [transcription]);

  const activeTranscriptIndex = useMemo(() => {
    if (!transcription) return -1;
    return transcriptLines.findIndex(
      (line) => currentTime >= line.seconds && currentTime < line.seconds + 5
    );
  }, [currentTime, transcriptLines]);

  return (
    <div className={styles.container}>
      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          onClick={() => setActiveRightTab("comments")}
          className={classNames(styles.tab, {
            [styles.tabActive]: activeRightTab === "comments",
          })}
        >
          <MessageSquare size={14} /> Comments{" "}
          <span className={styles.commentCount}>{comments.length}</span>
        </button>
        {isTimeFrameAsset && (
          <button
            onClick={() => setActiveRightTab("transcript")}
            className={classNames(styles.tab, {
              [styles.tabActive]: activeRightTab === "transcript",
            })}
          >
            <FileText size={14} /> Transcript
          </button>
        )}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {activeRightTab === "comments" && (
          <>
            {allComments.length === 0 ? (
              <div className={styles.empty}>
                <MessageSquare size={24} className={styles.emptyIcon} />
                <p className={styles.emptyText}>No comments yet.</p>
              </div>
            ) : (
              <div className={styles.commentsList}>
                {allComments.map((comment) => {
                  const isResolved = comment.isResolved ?? false;
                  const isOwner = isUserComment(comment);
                  const hasDrawing = (comment.drawings?.length ?? 0) > 0;
                  const commentTime = comment.timestamp ?? -1;

                  return (
                    <div
                      key={comment._id}
                      className={classNames(styles.commentCard, {
                        [styles.commentCardResolved]: isResolved,
                        [styles.commentCardHighlighted]:
                          activeCommentId === comment._id,
                      })}
                      onClick={() =>
                        jumpToTime(
                          commentTime >= 0 ? commentTime : 0,
                          comment._id
                        )
                      }
                    >
                      <div className={styles.commentCardTop}>
                        <div className={styles.commentAuthorInfo}>
                          <span className={styles.commentAuthorName}>
                            {comment.commentedBy?.firstName}{" "}
                            {comment.commentedBy?.lastName}
                          </span>
                          <span className={styles.commentAuthorType}>
                            ({comment.commentedByType || "User"})
                          </span>
                        </div>
                        <span className={styles.commentTimestamp}>
                          {dayjs(comment.createdAt).format("MMM DD, YYYY")}
                        </span>
                      </div>
                      <p
                        className={classNames(styles.commentText, {
                          [styles.commentTextResolved]: isResolved,
                        })}
                      >
                        {comment.text}
                      </p>
                      <div className={styles.commentBadges}>
                        {commentTime >= 0 && isTimeFrameAsset && (
                          <span className={styles.commentBadge}>
                            <Clock size={10} /> {formatTime(commentTime)}
                          </span>
                        )}
                        {hasDrawing && (
                          <span className={styles.commentBadgeDrawing}>
                            <Pencil size={10} /> Drawing Included
                          </span>
                        )}
                      </div>
                      {isOwner && (
                        <div className={styles.commentActions}>
                          <button
                            onClick={(e) =>
                              handleResolveComment(e, comment._id)
                            }
                            className={classNames(styles.commentActionButton, {
                              [styles.commentResolveButton]: true,
                              [styles.commentResolveButtonActive]: isResolved,
                            })}
                            disabled={isResolving}
                          >
                            <CheckCircle2 size={14} />
                          </button>
                          <button
                            onClick={(e) => handleDeleteComment(e, comment._id)}
                            className={classNames(
                              styles.commentActionButton,
                              styles.commentDeleteButton
                            )}
                            disabled={isDeleting}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={commentsEndRef} />
              </div>
            )}
          </>
        )}

        {activeRightTab === "transcript" && (
          <>
            {transcriptionStatus === "Pending" ||
            transcriptionStatus === "idle" ? (
              <div className={styles.transcriptEmpty}>
                {transcriptionStatus === "Pending" ? (
                  <>
                    <Loader2 size={28} className={styles.transcriptLoader} />
                    <p className={styles.transcriptEmptyTitle}>
                      Transcribing Audio...
                    </p>
                  </>
                ) : (
                  <>
                    <div className={styles.transcriptEmptyIcon}>
                      <Sparkles size={20} />
                    </div>
                    <h4 className={styles.transcriptEmptyTitle}>
                      Generate Transcript
                    </h4>
                    <button
                      className={styles.transcriptGenerateButton}
                      onClick={() => {
                        // TODO: Implement generate transcript
                      }}
                    >
                      <Sparkles size={14} /> Generate
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.transcriptList}>
                {transcriptLines.map((item, i) => (
                  <div
                    key={i}
                    className={classNames(styles.transcriptItem, {
                      [styles.transcriptItemActive]:
                        activeTranscriptIndex === i,
                    })}
                    onClick={() => jumpToTime(item.seconds)}
                  >
                    {activeTranscriptIndex === i && (
                      <div className={styles.transcriptIndicator} />
                    )}
                    <div className={styles.transcriptContent}>
                      <span
                        className={classNames(styles.transcriptTime, {
                          [styles.transcriptTimeActive]:
                            activeTranscriptIndex === i,
                        })}
                      >
                        {item.timeStr}
                      </span>
                      <span
                        className={classNames(styles.transcriptText, {
                          [styles.transcriptTextActive]:
                            activeTranscriptIndex === i,
                        })}
                      >
                        {item.text}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {/* Comment Input */}
      {activeRightTab === "comments" && (
        <div className={styles.commentInputArea}>
          <div
            className={classNames(styles.commentModeIndicator, {
              [styles.commentModeIndicatorTimestamp]: !isGeneralComment,
              [styles.commentModeIndicatorGeneral]: isGeneralComment,
            })}
          >
            <div className={styles.commentModeIndicatorLeft}>
              {!isGeneralComment ? (
                <>
                  <Clock size={10} />
                  Commenting at{" "}
                  <span className={styles.commentModeTime}>
                    {formatTime(currentTime)}
                  </span>
                </>
              ) : (
                <>
                  <MessageSquare size={10} />
                  <span className={styles.commentModeTime}>
                    General Comment
                  </span>
                </>
              )}
            </div>
            {!isGeneralComment ? (
              <button
                onClick={() => {
                  setIsGeneralMode(true);
                  if (isTimeFrameAsset) {
                    onClearDraftDrawings();
                    onDrawingEnabledChange(false);
                  }
                }}
                className={styles.commentModeClose}
              >
                <X size={10} />
              </button>
            ) : (
              <>
                {isTimeFrameAsset && (
                  <button
                    onClick={() => setIsGeneralMode(false)}
                    className={styles.commentModeSwitch}
                  >
                    Timestamp {formatTime(currentTime)}
                  </button>
                )}
              </>
            )}
          </div>

          <form onSubmit={handlePostComment} className={styles.commentForm}>
            <Input
              id="comment-input"
              value={commentDraft}
              onChange={handleCommentChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handlePostComment(e);
                }
              }}
              placeholder="Add comment..."
              suffix={
                <Space>
                  <button
                    type="button"
                    onClick={() => {
                      onDrawingEnabledChange(!isDrawingEnabled);

                      if (isDrawingEnabled) {
                        onClearDraftDrawings();
                      }
                      if (!isDrawingEnabled && isTimeFrameAsset) {
                        setIsGeneralMode(false);
                      }
                    }}
                    className={classNames(styles.commentDrawingButton, {
                      [styles.commentDrawingButtonActive]: isDrawingEnabled,
                    })}
                    title={
                      isDrawingEnabled
                        ? "Exit Drawing Mode"
                        : "Enable Drawing Mode"
                    }
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="submit"
                    disabled={!commentDraft.trim()}
                    className={styles.commentSendButton}
                  >
                    <Send size={12} />
                  </button>
                </Space>
              }
            />
          </form>
        </div>
      )}
    </div>
  );
};
